import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import TI from '../components/brand/TI';
import AgMark from '../components/brand/AgMark';
import InquiryDialog from '../components/InquiryDialog';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';

// ─── Editorial palette (locked) ──────────────────────────────────────
const SLATE = '#0B1426';
const INK = '#1B2538';
const PAPER = '#FFFFFF';
const IVORY = '#FBF8F1';
const HAIRLINE = '#ECECEC';
const MUTED = '#5B6577';
const CORAL = '#FF4E6A';
const AG_RED = '#E63946';

// ─── SEO content blocks ──────────────────────────────────────────────
const SEO_BLOCKS = [
  {
    slug: 'cosmetic-safety-assessment',
    title: 'Cosmetic Safety Assessment',
    body: 'Comprehensive evaluation of cosmetic and personal care products to confirm they pose no risk to human health under normal and reasonably foreseeable conditions of use. Each assessment aligns to global standards (EU 1223 Part A & B, MoCRA, FDA, Health Canada), providing the scientific foundation for market entry, retailer onboarding, and regulatory defensibility.',
  },
  {
    slug: 'toxicological-risk-assessment',
    title: 'Toxicological Risk Assessment (TRA)',
    body: 'Formalized hazard-and-exposure evaluation that quantifies the toxicological burden of each ingredient at the formulation level. We derive Points of Departure (PoD), apply route-specific uncertainty factors, and produce defensible Margin of Safety (MoS) calculations suitable for retailer review and regulatory submission.',
  },
  {
    slug: 'quantitative-risk-assessment',
    title: 'Quantitative Risk Assessment (QRA)',
    body: 'Data-driven derivation of safe exposure thresholds — essential for sensitizers, fragrance allergens, and ingredients with dose-response endpoints. Our QRA models follow IFRA, SCCS, and OECD guidance to ensure formulas remain within scientifically validated safety boundaries across leave-on and rinse-off product categories.',
  },
  {
    slug: 'exposure-based-risk-modeling',
    title: 'Exposure-Based Risk Modeling',
    body: 'Dynamic modeling of how consumers actually interact with a product. We factor application area, daily frequency, retention factor, age cohort, and skin condition to calculate precise systemic exposure dose (SED), localized exposure, and aggregate cross-product exposure where relevant.',
  },
  {
    slug: 'ingredient-screening',
    title: 'Ingredient Screening',
    body: 'Proactive evaluation of every raw material against global restricted substance lists, emerging chemicals of concern, and proprietary reliability tiers — before formulation begins. Outputs include risk-ranked ingredient profiles, alternative recommendations, and supplier-readable rationales.',
  },
  {
    slug: 'pregnancy-breastfeeding-evaluations',
    title: 'Pregnancy & Breastfeeding Evaluations',
    body: 'Specialized toxicological reviews focused on reproductive, developmental, and lactation endpoints. We provide the scientific substantiation needed to address vulnerable consumer populations with confidence, including endocrine activity flags, placental transfer considerations, and clear use-during-pregnancy positioning.',
  },
  {
    slug: 'retailer-readiness-documentation',
    title: 'Retailer Readiness Documentation',
    body: 'Turnkey compliance dossiers tailored to the specific scientific and documentary expectations of Sephora Clean, Credo Standard, Whole Foods Premium Body Care, Target Standards, and Ulta Conscious Beauty. We translate complex toxicological data into the exact formats demanded by each marketplace.',
  },
  {
    slug: 'product-safety-intelligence',
    title: 'Product Safety Intelligence',
    body: 'Beyond compliance, ToxIntel delivers strategic intelligence — anticipating regulatory shifts, retailer policy updates, and consumer expectations. Each output is engineered to remain a trusted reference across the product lifecycle.',
  },
  {
    slug: 'regulatory-toxicology-support',
    title: 'Regulatory Toxicology Support',
    body: 'Expert navigation of global chemical regulations: MoCRA (US), EU Cosmetic Regulation 1223/2009, Health Canada Cosmetic Notification, ASEAN harmonization, and California Proposition 65. We bridge scientific safety data with the legal compliance language required across jurisdictions.',
  },
  {
    slug: 'claims-substantiation',
    title: 'Claims Substantiation',
    body: 'Scientific validation of safety and efficacy claims — from "dermatologist-tested" and "non-comedogenic" to "pregnancy-safe" and "free-from." We assemble the objective evidence required to defend marketing language against regulator and class-action scrutiny.',
  },
  {
    slug: 'mocra-readiness',
    title: 'MoCRA Readiness',
    body: 'Operational alignment with the Modernization of Cosmetics Regulation Act. Adverse event reporting frameworks, safety substantiation files, responsible person designation, facility registration, and ingredient statement workflows — all delivered as audit-ready artifacts.',
  },
  {
    slug: 'supplier-ingredient-governance',
    title: 'Supplier & Ingredient Governance',
    body: 'End-to-end oversight of the chemical supply chain. We help establish supplier safety standards, conduct raw-material reliability reviews, and provide ongoing governance so consistency and compliance are maintained at the source — not discovered downstream.',
  },
];

// ─── FAQ content ─────────────────────────────────────────────────────
const FAQS = [
  {
    q: 'Is ToxIntel only for enterprise companies?',
    a: 'No. The platform scales from independent brand founders and consultants through to global enterprise organizations. Smaller brands typically begin with a single Risk Screen or claims-substantiation deliverable; larger operations adopt the platform for portfolio governance and supplier oversight.',
  },
  {
    q: 'What jurisdictions and regulations does ToxIntel cover?',
    a: 'Outputs are aligned to MoCRA (US), EU Cosmetic Regulation 1223/2009 (Parts A and B), Health Canada Cosmetic Notification, ASEAN harmonized requirements, California Proposition 65, and IFRA fragrance category mapping. Retailer-specific overlays include Sephora Clean, Credo, Whole Foods, Target, and Ulta.',
  },
  {
    q: 'Who signs the toxicological assessments?',
    a: 'Every formal assessment is reviewed and signed by qualified toxicologists at Aᴳ. The platform supports the work; the human scientific judgment is what gets delivered.',
  },
  {
    q: 'How is ToxIntel different from a hazard-scoring app?',
    a: 'Hazard scoring evaluates ingredients in isolation. ToxIntel performs exposure-based risk assessment — accounting for concentration, route, frequency, and product type — which is the methodology actually required by regulators and serious retailers.',
  },
  {
    q: 'How long does a typical assessment take?',
    a: 'Risk Screens turn around in 3 business days. Full toxicological assessments (EC 1223 Part A + B) typically complete within 7–10 business days. Rush capacity is available on request.',
  },
  {
    q: 'Can ToxIntel integrate with our PLM, QMS, or ERP?',
    a: 'Yes — API access and SSO are available for portfolio-tier engagements. Standard integrations cover Centric, PTC, Wrike, Notion, Airtable, and Google Workspace; custom integrations are scoped during onboarding.',
  },
  {
    q: 'What does "Independent scientific review powered by Aᴳ" mean?',
    a: 'Aᴳ — the Agyekum Group — is the parent scientific consultancy. ToxIntel is the platform Aᴳ uses to deliver its work to clients at scale. The reviews and signatures come from Aᴳ; the platform handles intake, workflow, documentation, and delivery.',
  },
  {
    q: 'How do I get started?',
    a: 'Submit a Partnership Inquiry below. A member of the Aᴳ team will respond within one business day to scope your specific needs, timeline, and category.',
  },
];

// ─── Built For — multi-tier governance architecture ─────────────────
const TIERS = [
  { tier: 'Independent', value: 'Establish baseline safety and market readiness without enterprise overhead. Single Risk Screen or assessment, no commitment.' },
  { tier: 'Growing', value: 'Retailer-ready documentation and complex exposure modeling for expanding product lines. Cross-formula governance.' },
  { tier: 'Manufacturer', value: 'Ingredient screening and bulk compliance at the raw-material level. Supplier-side governance and reliability tiers.' },
  { tier: 'Retailer', value: 'Vendor verification, marketplace governance, and overarching safety review for portfolios of third-party brands.' },
  { tier: 'Enterprise', value: 'Comprehensive portfolio risk management, global MoCRA governance, API integration, and white-label delivery.' },
];

// ─── Capabilities — 4-up above the fold ─────────────────────────────
const DELIVERABLES = [
  { title: 'Toxicological risk assessment', body: 'Comprehensive TRA / QRA modeling with route-specific Margin of Safety calculations for global compliance.' },
  { title: 'Regulatory documentation', body: 'Retailer-ready dossiers mapped to MoCRA, EU 1223 Part A & B, Prop 65, IFRA, and major beauty retailers.' },
  { title: 'Formula screening', body: 'Proactive raw material, impurity, and contaminant evaluation against global restricted-substance lists.' },
  { title: 'Claim substantiation', body: 'Defensible scientific backing for safety, efficacy, and "free-from" claims across categories.' },
];

const REGULATORY_LABELS = ['MoCRA', 'EU 1223', 'Prop 65', 'IFRA', 'Sephora', 'Credo', 'Whole Foods', 'Target', 'Ulta', 'Hale'];

// ─── Page ───────────────────────────────────────────────────────────
export default function EnterpriseLanding() {
  const { isAuthenticated, isAdmin } = useAuth();
  const portalTarget = isAdmin ? '/assessor' : '/dashboard';
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [activeTopic, setActiveTopic] = useState(SEO_BLOCKS[0].slug);

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: PAPER, color: SLATE }}>
      <Helmet>
        <title>ToxIntel · Toxicology Intelligence Platform by Aᴳ</title>
        <meta name="description" content="ToxIntel by Aᴳ — scientific safety intelligence for beauty, wellness, and consumer brands. Toxicological risk assessment, exposure modeling, MoCRA / EU 1223 / Prop 65 / IFRA alignment, and audit-ready compliance documentation." />
        <meta name="keywords" content="ToxIntel, toxicology intelligence platform, cosmetic safety assessment, TRA, QRA, exposure-based risk modeling, MoCRA, EU 1223, Proposition 65, IFRA, claims substantiation, retailer readiness, Aᴳ, Agyekum Group" />
        <meta name="robots" content="index,follow,max-image-preview:large" />
        <link rel="canonical" href="https://toxintel.tagc.us/" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="ToxIntel" />
        <meta property="og:title" content="ToxIntel · Toxicology Intelligence Platform by Aᴳ" />
        <meta property="og:description" content="Scientific safety intelligence for beauty, wellness, and consumer brands. Exposure-based safety, risk, and compliance intelligence — independent scientific review powered by Aᴳ." />
        <meta property="og:url" content="https://toxintel.tagc.us/" />
        <meta property="og:image" content="https://toxintel.tagc.us/toxintel-og.png" />
        <meta property="og:image:secure_url" content="https://toxintel.tagc.us/toxintel-og.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="ToxIntel by Aᴳ — Toxicology Intelligence Platform" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://toxintel.tagc.us/toxintel-og.png" />
        <meta name="twitter:image:alt" content="ToxIntel by Aᴳ — Toxicology Intelligence Platform" />
        <link rel="icon" type="image/png" sizes="512x512" href="/toxintel-favicon.png" />
        <link rel="shortcut icon" href="/toxintel-favicon.ico" />
        <link rel="apple-touch-icon" href="/toxintel-apple-touch-icon.png" />
        {/* Schema — Organization */}
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org', '@type': 'Organization',
          name: 'ToxIntel', alternateName: 'ToxIntel by Aᴳ',
          url: 'https://toxintel.tagc.us/',
          logo: 'https://toxintel.tagc.us/toxintel-favicon.png',
          parentOrganization: { '@type': 'Organization', name: 'The Agyekum Group', url: 'https://tagc.us/' },
          sameAs: ['https://tagc.us/'],
          description: 'Scientific safety intelligence platform for beauty, wellness, and consumer brands — exposure-based toxicological risk assessment, regulatory documentation, and claims substantiation.',
        })}</script>
        {/* Schema — FAQ */}
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org', '@type': 'FAQPage',
          mainEntity: FAQS.map((f) => ({
            '@type': 'Question', name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        })}</script>
      </Helmet>

      {/* ─── Header — sticky, glass, login de-emphasized ───────────── */}
      <header
        className="sticky top-0 z-40 backdrop-blur-xl"
        style={{ backgroundColor: 'rgba(255,255,255,0.92)', borderBottom: `1px solid ${HAIRLINE}` }}
        data-testid="main-header"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-4 flex items-center justify-between gap-4">
          {/* Brand lockup — Aᴳ parent logo navigates to tagc.us (same tab);
              ToxIntel wordmark + "by Aᴳ" attribution navigate to the product
              home so the parent/product hierarchy is preserved. */}
          <div className="flex items-center gap-3" data-testid="brand-mark">
            <a
              href="https://tagc.us"
              className="flex items-center"
              data-testid="brand-mark-ag"
              aria-label="Aᴳ — The Agyekum Group"
            >
              <AgMark size={34} variant="light" />
            </a>
            <Link
              to="/"
              className="flex items-center gap-3"
              data-testid="brand-mark-toxintel"
              aria-label="ToxIntel home"
            >
              <span
                className="text-xl sm:text-2xl tracking-tight"
                style={{ color: SLATE, fontFamily: '"Cormorant Garamond", "Times New Roman", serif' }}
              >
                <TI color={CORAL} />
              </span>
              <span
                className="text-[9px] font-mono uppercase tracking-[0.25em] hidden sm:inline"
                style={{ color: MUTED }}
                data-testid="brand-mark-attribution"
              >
                by A<sup className="text-[8px]">G</sup>
              </span>
            </Link>
          </div>
          <nav className="flex items-center gap-2 sm:gap-6">
            <a
              href="#capabilities"
              className="hidden md:inline text-[12px] uppercase tracking-[0.22em] transition-colors"
              style={{ color: MUTED }}
              onMouseEnter={(e) => { e.currentTarget.style.color = SLATE; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = MUTED; }}
            >Platform</a>
            <a
              href="#built-for"
              className="hidden md:inline text-[12px] uppercase tracking-[0.22em] transition-colors"
              style={{ color: MUTED }}
              onMouseEnter={(e) => { e.currentTarget.style.color = SLATE; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = MUTED; }}
            >Built for</a>
            <a
              href="#regulatory"
              className="hidden lg:inline text-[12px] uppercase tracking-[0.22em] transition-colors"
              style={{ color: MUTED }}
              onMouseEnter={(e) => { e.currentTarget.style.color = SLATE; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = MUTED; }}
            >Regulatory</a>
            <a
              href="#faq"
              className="hidden md:inline text-[12px] uppercase tracking-[0.22em] transition-colors"
              style={{ color: MUTED }}
              onMouseEnter={(e) => { e.currentTarget.style.color = SLATE; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = MUTED; }}
            >FAQ</a>
            {isAuthenticated ? (
              <Link
                to={portalTarget}
                data-testid="header-cta-portal"
                className="inline-flex items-center justify-center h-9 px-4 rounded-full text-[11px] font-bold uppercase tracking-[0.2em] transition-colors"
                style={{ backgroundColor: SLATE, color: '#FFFFFF' }}
              >
                Open portal →
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  data-testid="header-cta-signin"
                  className="hidden sm:inline text-[12px] tracking-tight transition-colors"
                  style={{ color: MUTED }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = SLATE; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = MUTED; }}
                >
                  Existing client? Sign in →
                </Link>
                <button
                  type="button"
                  onClick={() => setInquiryOpen(true)}
                  data-testid="header-cta-inquiry"
                  className="inline-flex items-center justify-center h-9 px-4 rounded-full text-[11px] font-bold uppercase tracking-[0.2em] transition-colors"
                  style={{ backgroundColor: SLATE, color: '#FFFFFF' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#000000'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = SLATE; }}
                >
                  Partner inquiry
                </button>
              </>
            )}
          </nav>
        </div>
      </header>

      <main className="flex-1">
        {/* ─── HERO — wide container, dense above the fold ─────────── */}
        <section className="px-5 sm:px-8 lg:px-12 pt-14 sm:pt-20 lg:pt-24 pb-12 sm:pb-16 lg:pb-20" data-testid="hero-section">
          <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-8">
              <p
                className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.32em] mb-5 sm:mb-7"
                style={{ color: CORAL }}
                data-testid="hero-eyebrow"
              >
                <TI color={CORAL} /> &middot; by Aᴳ
              </p>
              <h1
                className="font-light leading-[1.04] tracking-tight mb-7 sm:mb-9"
                style={{
                  color: SLATE,
                  fontFamily: '"Cormorant Garamond", "Times New Roman", serif',
                  fontSize: 'clamp(2.4rem, 5.5vw, 4.5rem)',
                }}
                data-testid="hero-h1"
              >
                Scientific safety intelligence for{' '}
                <em className="not-italic italic" style={{ color: CORAL }}>
                  beauty, wellness, and consumer
                </em>{' '}
                brands.
              </h1>
              <p
                className="text-base sm:text-lg leading-relaxed max-w-2xl mb-4"
                style={{ color: MUTED }}
              >
                Exposure-based safety, risk, and compliance intelligence — built
                for independent brands, growing brands, manufacturers, retailers,
                and enterprise organizations. Independent scientific review powered by&nbsp;Aᴳ.
              </p>
              <p
                className="text-[11px] sm:text-[12px] italic leading-relaxed mb-9 max-w-2xl"
                style={{ color: MUTED, fontFamily: '"Cormorant Garamond", "Times New Roman", serif' }}
                data-testid="hero-credibility-line"
              >
                Developed from real-world toxicology, retailer, and regulatory workflows.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => setInquiryOpen(true)}
                  data-testid="hero-cta-inquiry"
                  className="inline-flex items-center justify-center h-12 px-7 rounded-full text-[11px] font-bold uppercase tracking-[0.22em] transition-colors"
                  style={{ backgroundColor: CORAL, color: '#FFFFFF' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#C9192C'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = CORAL; }}
                >
                  Request partnership inquiry →
                </button>
                <a
                  href="#capabilities"
                  data-testid="hero-cta-explore"
                  className="inline-flex items-center justify-center h-12 px-7 rounded-full text-[11px] font-bold uppercase tracking-[0.22em] transition-all"
                  style={{ backgroundColor: PAPER, color: INK, border: `1px solid ${HAIRLINE}` }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#D6D6D6'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = HAIRLINE; }}
                >
                  Explore platform
                </a>
              </div>
            </div>
            {/* Right column — quiet credentials block (desktop only) */}
            <aside className="lg:col-span-4 hidden lg:block" data-testid="hero-aside">
              <div
                className="p-6 rounded-sm"
                style={{ backgroundColor: IVORY, border: `1px solid ${HAIRLINE}` }}
              >
                <p className="text-[10px] font-bold uppercase tracking-[0.28em] mb-4" style={{ color: CORAL }}>
                  Scientific governance
                </p>
                <ul className="space-y-3.5">
                  {[
                    'Every assessment signed by Aᴳ toxicologists',
                    'Aligned to MoCRA, EU 1223, Prop 65, IFRA',
                    'Retailer-mapped: Sephora, Credo, Whole Foods, Target, Ulta',
                    'Exposure-based — not hazard-only scoring',
                  ].map((line) => (
                    <li key={line} className="flex items-start gap-2.5 text-[13px] leading-relaxed" style={{ color: SLATE }}>
                      <span className="mt-1.5 inline-block w-1 h-1 rounded-full flex-shrink-0" style={{ backgroundColor: CORAL }} />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </section>

        {/* ─── Trusted-by bar ───────────────────────────────────────── */}
        <section
          className="px-5 sm:px-8 lg:px-12 py-6 sm:py-8"
          style={{ borderTop: `1px solid ${HAIRLINE}`, borderBottom: `1px solid ${HAIRLINE}`, backgroundColor: PAPER }}
          data-testid="trusted-by-bar"
        >
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.28em]" style={{ color: MUTED }}>
              Trusted by formulators, brands, and retailers
            </p>
            <div className="text-center sm:text-right">
              <p className="text-[11px] sm:text-[12px] tracking-tight" style={{ color: SLATE }} data-testid="trusted-by-list">
                Hale &nbsp;·&nbsp; Sephora &nbsp;·&nbsp; Credo &nbsp;·&nbsp; Whole&nbsp;Foods &nbsp;·&nbsp; Target &nbsp;·&nbsp; Ulta &nbsp;·&nbsp; Contract&nbsp;manufacturers &nbsp;·&nbsp; Independent&nbsp;brands &nbsp;·&nbsp; Enterprise&nbsp;organizations
              </p>
              <p className="text-[9px] uppercase tracking-[0.2em] mt-1.5" style={{ color: MUTED }} data-testid="trusted-by-hale-note">
                Hale — Hale League partner ecosystem
              </p>
            </div>
          </div>
        </section>

        {/* ─── Capabilities — 4-up grid ─────────────────────────────── */}
        <section
          id="capabilities"
          className="px-5 sm:px-8 lg:px-12 py-16 sm:py-20 lg:py-24"
          style={{ backgroundColor: PAPER }}
          data-testid="capabilities"
        >
          <div className="max-w-7xl mx-auto">
            <div className="max-w-3xl mb-12 sm:mb-14">
              <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.32em] mb-3" style={{ color: CORAL }}>Platform capabilities</p>
              <h2
                className="font-light leading-[1.1] tracking-tight"
                style={{
                  color: SLATE,
                  fontFamily: '"Cormorant Garamond", "Times New Roman", serif',
                  fontSize: 'clamp(1.9rem, 4vw, 2.85rem)',
                }}
              >
                What ToxIntel delivers.
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px" style={{ backgroundColor: HAIRLINE }}>
              {DELIVERABLES.map((d, i) => (
                <div
                  key={d.title}
                  className="p-7 sm:p-8 flex flex-col gap-3"
                  style={{ backgroundColor: IVORY }}
                  data-testid={`capability-${i}`}
                >
                  <span className="text-[10px] font-mono tabular-nums" style={{ color: CORAL }}>{String(i + 1).padStart(2, '0')}</span>
                  <h3
                    className="text-[18px] sm:text-[20px] leading-tight"
                    style={{ color: SLATE, fontFamily: '"Cormorant Garamond", "Times New Roman", serif' }}
                  >
                    {d.title}
                  </h3>
                  <p className="text-[13px] leading-relaxed" style={{ color: MUTED }}>{d.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── AI-assisted / Toxicologist-governed strip ────────────── */}
        <section
          className="px-5 sm:px-8 lg:px-12 py-10 sm:py-14"
          style={{ borderTop: `1px solid ${HAIRLINE}`, borderBottom: `1px solid ${HAIRLINE}` }}
          data-testid="ai-governance-strip"
        >
          <div className="max-w-4xl mx-auto text-center">
            <p
              className="font-light leading-[1.2] tracking-tight mb-3"
              style={{ color: SLATE, fontFamily: '"Cormorant Garamond", "Times New Roman", serif', fontSize: 'clamp(1.4rem, 3vw, 2rem)' }}
            >
              AI-assisted infrastructure. <em className="italic" style={{ color: CORAL }}>Toxicologist-governed</em> review.
            </p>
            <p className="text-[12px] sm:text-[13px] leading-relaxed max-w-xl mx-auto" style={{ color: MUTED }}>
              Every output operates within a scientific governance framework developed by Aᴳ.
            </p>
          </div>
        </section>

        {/* ─── Workflow visualization ──────────────────────────────── */}
        <section
          className="px-5 sm:px-8 lg:px-12 py-16 sm:py-20"
          style={{ backgroundColor: IVORY }}
          data-testid="workflow-viz"
        >
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12 sm:mb-14">
              <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.32em] mb-3" style={{ color: CORAL }}>The intelligence workflow</p>
              <h2
                className="font-light leading-[1.1] tracking-tight"
                style={{ color: SLATE, fontFamily: '"Cormorant Garamond", "Times New Roman", serif', fontSize: 'clamp(1.9rem, 4vw, 2.85rem)' }}
              >
                From formula to governance.
              </h2>
            </div>
            <div className="flex flex-col lg:flex-row items-center justify-between gap-4 lg:gap-3">
              {['Formula', 'Exposure Review', 'Risk Interpretation', 'Documentation', 'Governance'].map((step, i, arr) => (
                <React.Fragment key={step}>
                  <div className="flex flex-col items-center text-center flex-1 px-2" data-testid={`workflow-step-${i}`}>
                    <span className="text-[10px] font-mono tabular-nums mb-2" style={{ color: CORAL }}>
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span
                      className="leading-tight"
                      style={{ color: SLATE, fontFamily: '"Cormorant Garamond", "Times New Roman", serif', fontSize: 'clamp(1.15rem, 1.8vw, 1.4rem)' }}
                    >
                      {step}
                    </span>
                  </div>
                  {i < arr.length - 1 && (
                    <span className="text-[14px] sm:text-[18px]" style={{ color: CORAL }} aria-hidden="true">
                      <span className="hidden lg:inline">→</span>
                      <span className="lg:hidden">↓</span>
                    </span>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </section>

        {/* ─── Built For — 5-tier governance architecture ─────────── */}
        <section
          id="built-for"
          className="px-5 sm:px-8 lg:px-12 py-16 sm:py-20 lg:py-24"
          style={{ backgroundColor: PAPER }}
          data-testid="built-for"
        >
          <div className="max-w-7xl mx-auto">
            <div className="max-w-3xl mb-12 sm:mb-14">
              <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.32em] mb-3" style={{ color: CORAL }}>Governance architecture</p>
              <h2
                className="font-light leading-[1.1] tracking-tight mb-4"
                style={{ color: SLATE, fontFamily: '"Cormorant Garamond", "Times New Roman", serif', fontSize: 'clamp(1.9rem, 4vw, 2.85rem)' }}
              >
                Scaled intelligence from single formulas to global portfolios.
              </h2>
              <p className="text-[14px] sm:text-[15px] leading-relaxed" style={{ color: MUTED }}>
                ToxIntel is engineered for the realities of where brands actually operate — not a one-size-fits-all enterprise contract.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-5">
              {TIERS.map((t, i) => (
                <div
                  key={t.tier}
                  className="p-6 sm:p-7 rounded-sm transition-all duration-200"
                  style={{ backgroundColor: PAPER, border: `1px solid ${HAIRLINE}` }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = SLATE; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = HAIRLINE; e.currentTarget.style.transform = 'translateY(0)'; }}
                  data-testid={`tier-${t.tier.toLowerCase()}`}
                >
                  <span className="text-[10px] font-mono tabular-nums block mb-3" style={{ color: CORAL }}>{String(i + 1).padStart(2, '0')}</span>
                  <h3
                    className="text-[18px] sm:text-[20px] leading-tight mb-3"
                    style={{ color: SLATE, fontFamily: '"Cormorant Garamond", "Times New Roman", serif' }}
                  >
                    {t.tier}
                  </h3>
                  <p className="text-[13px] leading-relaxed" style={{ color: MUTED }}>{t.value}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── Scientific Coverage — preserved from prior build ───── */}
        <section
          id="scientific-coverage"
          className="px-5 sm:px-8 lg:px-12 py-16 sm:py-20 lg:py-24"
          style={{ backgroundColor: IVORY }}
          data-testid="scientific-coverage"
        >
          <div className="max-w-7xl mx-auto">
            <div className="max-w-3xl mb-12 sm:mb-14">
              <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.32em] mb-3" style={{ color: CORAL }}>Scientific coverage</p>
              <h2
                className="font-light leading-[1.1] tracking-tight"
                style={{ color: SLATE, fontFamily: '"Cormorant Garamond", "Times New Roman", serif', fontSize: 'clamp(1.9rem, 4vw, 2.85rem)' }}
              >
                The scope of governance the platform supports.
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {[
                { title: 'TRA / QRA', body: 'Toxicological and quantitative risk assessment workflows.' },
                { title: 'Exposure modelling', body: 'Route-specific SED, NOAEL, and Margin of Safety derivation.' },
                { title: 'Packaging interaction review', body: 'Material-formula compatibility and migration governance.' },
                { title: 'Claims governance', body: 'Substantiation rationale for marketing and retailer claims.' },
                { title: 'MoCRA readiness', body: 'Operational alignment with US cosmetic regulation requirements.' },
                { title: 'IFRA alignment', body: 'Fragrance category mapping and concentration overlays.' },
                { title: 'Retailer documentation', body: 'Sephora · Credo · Whole Foods · Target · Ulta-aligned packs.' },
                { title: 'Ingredient intelligence', body: 'Hazard, restriction, and reliability-scored ingredient data.' },
                { title: 'Safety substantiation', body: 'Evidence assembly for product-safety conclusions.' },
              ].map((c) => (
                <article
                  key={c.title}
                  className="p-6 sm:p-7 rounded-sm transition-all duration-200"
                  style={{ backgroundColor: PAPER, border: `1px solid ${HAIRLINE}` }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#D6D6D6'; e.currentTarget.style.boxShadow = '0 6px 18px rgba(11,20,38,0.05)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = HAIRLINE; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <h3
                    className="text-[17px] sm:text-[19px] mb-2 leading-tight"
                    style={{ color: SLATE, fontFamily: '"Cormorant Garamond", "Times New Roman", serif' }}
                  >
                    {c.title}
                  </h3>
                  <p className="text-[13px] leading-relaxed" style={{ color: MUTED }}>{c.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ─── Regulatory readiness — typographic logo strip ─────── */}
        <section
          id="regulatory"
          className="px-5 sm:px-8 lg:px-12 py-14 sm:py-18"
          style={{ backgroundColor: PAPER, borderTop: `1px solid ${HAIRLINE}` }}
          data-testid="regulatory-readiness"
        >
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-8 sm:mb-10">
              <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.32em] mb-3" style={{ color: CORAL }}>Regulatory, retailer &amp; marketplace readiness</p>
              <h2
                className="font-light leading-[1.1] tracking-tight"
                style={{ color: SLATE, fontFamily: '"Cormorant Garamond", "Times New Roman", serif', fontSize: 'clamp(1.6rem, 3vw, 2rem)' }}
              >
                Aligned with the standards modern brands navigate every day.
              </h2>
              <p className="text-[13px] sm:text-[14px] leading-relaxed mt-4 max-w-2xl mx-auto" style={{ color: MUTED }}>
                Scientific governance designed to support retailer onboarding, marketplace verification, and regulatory expectations across global jurisdictions.
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-5">
              {REGULATORY_LABELS.map((label) => (
                <span
                  key={label}
                  className="inline-block px-4 py-2 text-[11px] sm:text-[12px] font-bold uppercase tracking-[0.22em] rounded-sm"
                  style={{ color: SLATE, border: `1px solid ${HAIRLINE}`, backgroundColor: PAPER }}
                  data-testid={`reg-${label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {label}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ─── SEO content blocks — TOC + reading flow (desktop), accordion (mobile) ─── */}
        <section
          className="px-5 sm:px-8 lg:px-12 py-16 sm:py-20 lg:py-24"
          style={{ backgroundColor: PAPER, borderTop: `1px solid ${HAIRLINE}` }}
          data-testid="seo-content-blocks"
        >
          <div className="max-w-7xl mx-auto">
            <div className="max-w-3xl mb-12 sm:mb-14">
              <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.32em] mb-3" style={{ color: CORAL }}>Reference</p>
              <h2
                className="font-light leading-[1.1] tracking-tight mb-4"
                style={{ color: SLATE, fontFamily: '"Cormorant Garamond", "Times New Roman", serif', fontSize: 'clamp(1.9rem, 4vw, 2.85rem)' }}
              >
                Safety &amp; compliance terminology.
              </h2>
              <p className="text-[14px] sm:text-[15px] leading-relaxed" style={{ color: MUTED }}>
                Brief, authoritative definitions of the scientific and regulatory work covered by the platform.
              </p>
            </div>

            {/* Desktop: 12-col sticky TOC + reading column. Mobile: accordion. */}
            <div className="hidden lg:grid grid-cols-12 gap-12">
              <nav className="col-span-4 xl:col-span-3 sticky top-24 self-start" aria-label="Section index">
                <p className="text-[10px] font-bold uppercase tracking-[0.28em] mb-4" style={{ color: MUTED }}>Topics</p>
                <ul className="space-y-2.5" style={{ borderLeft: `1px solid ${HAIRLINE}` }}>
                  {SEO_BLOCKS.map((b) => (
                    <li key={b.slug}>
                      <a
                        href={`#${b.slug}`}
                        onClick={() => setActiveTopic(b.slug)}
                        className="block pl-4 py-1 text-[13px] leading-snug transition-colors"
                        style={{
                          color: activeTopic === b.slug ? SLATE : MUTED,
                          borderLeft: activeTopic === b.slug ? `2px solid ${CORAL}` : '2px solid transparent',
                          marginLeft: '-1px',
                        }}
                        data-testid={`toc-${b.slug}`}
                      >
                        {b.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
              <div className="col-span-8 xl:col-span-9 space-y-12">
                {SEO_BLOCKS.map((b) => (
                  <article key={b.slug} id={b.slug} className="scroll-mt-28" data-testid={`seo-block-${b.slug}`}>
                    <h3
                      className="text-[24px] xl:text-[28px] tracking-tight mb-3 leading-tight"
                      style={{ color: SLATE, fontFamily: '"Cormorant Garamond", "Times New Roman", serif' }}
                    >
                      {b.title}
                    </h3>
                    <p className="text-[15px] leading-relaxed max-w-2xl" style={{ color: INK }}>
                      {b.body}
                    </p>
                  </article>
                ))}
              </div>
            </div>
            {/* Mobile accordion */}
            <div className="lg:hidden">
              <Accordion type="single" collapsible defaultValue={SEO_BLOCKS[0].slug}>
                {SEO_BLOCKS.map((b) => (
                  <AccordionItem key={b.slug} value={b.slug} className="border-b" style={{ borderColor: HAIRLINE }}>
                    <AccordionTrigger
                      className="text-left text-[15px] py-4"
                      style={{ color: SLATE, fontFamily: '"Cormorant Garamond", "Times New Roman", serif' }}
                      data-testid={`acc-${b.slug}`}
                    >
                      {b.title}
                    </AccordionTrigger>
                    <AccordionContent className="pb-4 text-[13.5px] leading-relaxed" style={{ color: INK }}>
                      {b.body}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* ─── FAQ accordion ───────────────────────────────────────── */}
        <section
          id="faq"
          className="px-5 sm:px-8 lg:px-12 py-16 sm:py-20 lg:py-24"
          style={{ backgroundColor: IVORY }}
          data-testid="faq-section"
        >
          <div className="max-w-4xl mx-auto">
            <div className="mb-10 sm:mb-12">
              <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.32em] mb-3" style={{ color: CORAL }}>Frequently asked</p>
              <h2
                className="font-light leading-[1.1] tracking-tight"
                style={{ color: SLATE, fontFamily: '"Cormorant Garamond", "Times New Roman", serif', fontSize: 'clamp(1.9rem, 4vw, 2.85rem)' }}
              >
                Common questions.
              </h2>
            </div>
            <Accordion type="single" collapsible>
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-b" style={{ borderColor: HAIRLINE }}>
                  <AccordionTrigger
                    className="text-left text-[15px] sm:text-[16px] py-5 font-medium"
                    style={{ color: SLATE }}
                    data-testid={`faq-q-${i}`}
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent
                    className="pb-5 text-[14px] sm:text-[15px] leading-relaxed"
                    style={{ color: INK, fontFamily: '"Cormorant Garamond", "Times New Roman", serif' }}
                  >
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* ─── Bottom CTA + credentials ────────────────────────────── */}
        <section
          className="px-5 sm:px-8 lg:px-12 py-16 sm:py-20"
          style={{ backgroundColor: PAPER, borderTop: `1px solid ${HAIRLINE}` }}
          data-testid="bottom-cta"
        >
          <div className="max-w-5xl mx-auto text-center">
            <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.32em] mb-4" style={{ color: CORAL }}>Begin</p>
            <h2
              className="font-light leading-[1.05] tracking-tight mb-6"
              style={{ color: SLATE, fontFamily: '"Cormorant Garamond", "Times New Roman", serif', fontSize: 'clamp(2rem, 4.5vw, 3rem)' }}
            >
              Bring scientific rigor to your brand.
            </h2>
            <p className="text-[14px] sm:text-[16px] leading-relaxed max-w-xl mx-auto mb-9" style={{ color: MUTED }}>
              A member of the Aᴳ team will respond within one business day to scope your specific category, jurisdiction, and timeline.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <button
                type="button"
                onClick={() => setInquiryOpen(true)}
                data-testid="bottom-cta-inquiry"
                className="inline-flex items-center justify-center h-12 px-7 rounded-full text-[11px] font-bold uppercase tracking-[0.22em] transition-colors"
                style={{ backgroundColor: CORAL, color: '#FFFFFF' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#C9192C'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = CORAL; }}
              >
                Request partnership inquiry →
              </button>
              <Link
                to="/login"
                data-testid="bottom-cta-signin"
                className="text-[12px] tracking-tight transition-colors"
                style={{ color: MUTED }}
                onMouseEnter={(e) => { e.currentTarget.style.color = SLATE; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = MUTED; }}
              >
                Existing client? Sign in →
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* ─── Footer ──────────────────────────────────────────────── */}
      <footer
        className="px-5 sm:px-8 lg:px-12 py-10 sm:py-12"
        style={{ backgroundColor: IVORY, borderTop: `1px solid ${HAIRLINE}` }}
        data-testid="main-footer"
      >
        <div className="max-w-7xl mx-auto flex flex-col gap-6">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
            <div className="flex items-center gap-3">
              <AgMark size={32} variant="light" />
              <span className="text-xl tracking-tight" style={{ color: SLATE, fontFamily: '"Cormorant Garamond", "Times New Roman", serif' }}>
                <TI color={CORAL} />
              </span>
              <span className="text-[9px] font-mono uppercase tracking-[0.25em]" style={{ color: MUTED }}>
                by A<sup className="text-[8px]">G</sup>
              </span>
            </div>
            <nav className="flex flex-wrap items-center gap-x-6 gap-y-2">
              {[
                ['Platform', '#capabilities'],
                ['Built for', '#built-for'],
                ['Regulatory', '#regulatory'],
                ['FAQ', '#faq'],
                ['Sign in', '/login'],
              ].map(([label, href]) => (
                href.startsWith('/') ? (
                  <Link key={label} to={href} className="text-[11px] uppercase tracking-[0.22em] transition-colors"
                    style={{ color: MUTED }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = SLATE; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = MUTED; }}
                  >
                    {label}
                  </Link>
                ) : (
                  <a key={label} href={href} className="text-[11px] uppercase tracking-[0.22em] transition-colors"
                    style={{ color: MUTED }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = SLATE; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = MUTED; }}
                  >
                    {label}
                  </a>
                )
              ))}
            </nav>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-6" style={{ borderTop: `1px solid ${HAIRLINE}` }}>
            <p className="text-[11px] italic" style={{ color: MUTED, fontFamily: '"Cormorant Garamond", "Times New Roman", serif' }} data-testid="footer-governance-attribution">
              Scientific governance provided by{' '}
              <a href="https://tagc.us" target="_blank" rel="noopener noreferrer"
                className="not-italic font-medium" style={{ color: SLATE, textDecoration: 'underline', textUnderlineOffset: '3px', textDecorationColor: HAIRLINE }}
                data-testid="footer-tagc-link"
              >
                A<sup className="text-[8px]">G</sup>
              </a>.
            </p>
            <p className="text-[10px] uppercase tracking-[0.28em]" style={{ color: MUTED }}>
              © {new Date().getFullYear()} The Agyekum Group · All rights reserved
            </p>
          </div>
        </div>
      </footer>

      <InquiryDialog open={inquiryOpen} onOpenChange={setInquiryOpen} />
    </div>
  );
}
